import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import usePageTitle from '../hooks/usePageTitle';
import TaxDeadlineCountdown from '../components/TaxDeadlineCountdown';
import FounderSection from '../components/FounderSection';
import {
  Shield, CheckCircle, ArrowRight, Star, Phone, MapPin,
  FileText, Building, Users, TrendingUp, BarChart, Clock,
  Award, Lock, Globe, ChevronRight, ChevronLeft
} from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const HomePage = () => {
  usePageTitle('Home');
  const { t, lang } = useLanguage();
  const [blogPosts, setBlogPosts] = useState([]);
  const [currentReviewIndex, setCurrentReviewIndex] = useState(0);

  useEffect(() => {
    axios.get(`${API}/blog/posts?limit=3`).then(r => setBlogPosts(r.data.posts || []));
  }, []);

  const services = [
    { icon: FileText, title: t('serviceIndividual'), desc: t('serviceIndividualDesc'), badge: t('mostPopular'), color: '#0B1F3A' },
    { icon: Building, title: t('serviceBusiness'), desc: t('serviceBusinessDesc'), color: '#2563EB' },
    { icon: Users, title: t('serviceSelfEmployed'), desc: t('serviceSelfEmployedDesc'), color: '#0B1F3A' },
    { icon: Globe, title: t('serviceMultiState'), desc: t('serviceMultiStateDesc'), color: '#2563EB' },
    { icon: TrendingUp, title: t('servicePlanning'), desc: t('servicePlanningDesc'), color: '#0B1F3A' },
    { icon: Shield, title: t('serviceAudit'), desc: t('serviceAuditDesc'), color: '#2563EB' },
  ];

  const whyChooseUs = [
    { icon: CheckCircle, title: t('personalizedService'), desc: t('personalizedServiceDesc') },
    { icon: Clock, title: t('yearRoundSupport'), desc: t('yearRoundSupportDesc') },
    { icon: Award, title: t('multiLayerReview'), desc: t('multiLayerReviewDesc') },
    { icon: Globe, title: t('nationwideCoverage'), desc: t('nationwideCoverageDesc') },
    { icon: Lock, title: t('encryptedSecure'), desc: t('encryptedSecureDesc') },
    { icon: TrendingUp, title: t('maximumRefund'), desc: t('maximumRefundDesc') },
  ];

  const steps = [
    { num: '01', title: t('step1Title'), desc: t('step1Desc') },
    { num: '02', title: t('step2Title'), desc: t('step2Desc') },
    { num: '03', title: t('step3Title'), desc: t('step3Desc') },
    { num: '04', title: t('step4Title'), desc: t('step4Desc') },
  ];

  const testimonials = [
    { name: 'Michael T.', role: lang === 'es' ? 'Dueño de Pequeño Negocio' : 'Small Business Owner', rating: 5, text: lang === 'es' ? 'Fajlur y su equipo encontraron deducciones que nunca supe que existían. La mejor experiencia fiscal que he tenido. Cambié de TurboTax y nunca voy a volver.' : "Fajlur and his team found deductions I never knew existed. Best tax experience I've had. Switched from TurboTax and never looking back." },
    { name: 'Sarah K.', role: lang === 'es' ? 'Trabajadora Remota, 3 Estados' : 'Remote Worker, 3 States', rating: 5, text: lang === 'es' ? 'Trabajo en múltiples estados y MaTaxPreparer manejó todo perfectamente. Gran reembolso este año y cero estrés.' : "I work across multiple states and MaTaxPreparer handled everything seamlessly. Huge refund this year and zero stress." },
    { name: 'David L.', role: lang === 'es' ? 'Diseñador Freelance' : 'Freelance Designer', rating: 5, text: lang === 'es' ? 'Como contratista 1099, estaba dejando dinero sobre la mesa. Encontraron deducciones que me perdí por años. Profesional, rápido y asequible.' : "As a 1099 contractor, I was leaving money on the table. They found deductions I missed for years. Professional, fast, and affordable." },
    { name: 'Jennifer M.', role: lang === 'es' ? 'Fundadora de Startup' : 'Startup Founder', rating: 5, text: lang === 'es' ? 'Configuraron la estructura fiscal de mi LLC y manejaron nuestro primer año de impuestos comerciales. Minuciosos, profesionales y realmente se preocupan.' : "They set up my LLC tax structure and handled our first year of business taxes. Thorough, professional, and they genuinely care." },
    { name: 'Robert H.', role: lang === 'es' ? 'Inversionista Inmobiliario' : 'Real Estate Investor', rating: 5, text: lang === 'es' ? 'Mis impuestos de propiedad de inversión son complejos. MaTaxPreparer los manejó impecablemente y me ahorró miles.' : "My investment property taxes are complex. MaTaxPreparer handled them flawlessly and saved me thousands." },
    { name: 'Lisa Chen', role: lang === 'es' ? 'Consultora de Healthcare' : 'Healthcare Consultant', rating: 5, text: lang === 'es' ? 'Respuesta rápida, servicio excelente. Encontraron deducciones de mi oficina en casa que había estado perdiendo.' : "Quick response, excellent service. They found home office deductions I'd been missing for years." },
    { name: 'Marcus W.', role: lang === 'es' ? 'Conductor de Uber/Lyft' : 'Uber/Lyft Driver', rating: 5, text: lang === 'es' ? 'Primer año de trabajo por cuenta propia y estaba perdido. Hicieron mis impuestos fáciles de entender y maximizaron mi reembolso.' : "First year self-employed and I was lost. They made my taxes easy to understand and maximized my refund." },
    { name: 'Amanda P.', role: lang === 'es' ? 'Enfermera, 2 Trabajos' : 'Nurse, 2 Jobs', rating: 5, text: lang === 'es' ? 'Con dos empleadores W-2, mi situación fiscal no era simple. Manejaron todo y me ahorraron tiempo y dinero.' : "With two W-2 employers, my tax situation wasn't simple. They handled everything and saved me time and money." },
    { name: 'James R.', role: lang === 'es' ? 'Contratista de Construcción' : 'Construction Contractor', rating: 5, text: lang === 'es' ? 'He usado varios preparadores de impuestos a lo largo de los años. MaTaxPreparer es por lejos el más profesional y minucioso.' : "I've used various tax preparers over the years. MaTaxPreparer is by far the most professional and thorough." },
    { name: 'Patricia G.', role: lang === 'es' ? 'Jubilada con Rentas' : 'Retiree with Rentals', rating: 5, text: lang === 'es' ? 'Impuestos de jubilación, seguro social, e ingresos de alquiler - manejaron todo con cuidado y me explicaron cada paso.' : "Retirement taxes, social security, and rental income - they handled it all with care and explained every step." },
    { name: 'Kevin B.', role: lang === 'es' ? 'Ingeniero de Software' : 'Software Engineer', rating: 5, text: lang === 'es' ? 'Portal en línea súper fácil de usar. Subí mis documentos, respondí algunas preguntas, y obtuve un gran reembolso.' : "Online portal was super easy to use. Uploaded my docs, answered some questions, and got a great refund." },
    { name: 'Nicole S.', role: lang === 'es' ? 'Dueña de Restaurante' : 'Restaurant Owner', rating: 5, text: lang === 'es' ? 'Impuestos de negocios pueden ser abrumadores. El equipo de Fajlur hizo el proceso sin dolor y encontró ahorros que no esperaba.' : "Business taxes can be overwhelming. Fajlur's team made the process painless and found savings I wasn't expecting." },
  ];

  // Auto-rotate reviews every 7 seconds
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentReviewIndex((prev) => (prev + 4) % testimonials.length);
    }, 7000);
    return () => clearInterval(interval);
  }, [testimonials.length]);

  const visibleTestimonials = [];
  for (let i = 0; i < 4; i++) {
    visibleTestimonials.push(testimonials[(currentReviewIndex + i) % testimonials.length]);
  }

  const nextReviews = () => setCurrentReviewIndex((prev) => (prev + 4) % testimonials.length);
  const prevReviews = () => setCurrentReviewIndex((prev) => (prev - 4 + testimonials.length) % testimonials.length);

  const stats = [
    { value: '500+', label: t('clientsServed'), sub: t('andGrowing') },
    { value: '50', label: t('statesCovered'), sub: t('nationwideService') },
    { value: '4+', label: t('yearsExperience'), sub: t('since') },
    { value: '100%', label: t('secureFiling'), sub: t('irsAuthorizedShort') },
  ];

  return (
    <div className="overflow-hidden">
      {/* ─── HERO ─── */}
      <section className="relative min-h-screen flex items-center" style={{ background: '#0B1F3A' }}>
        <div
          className="absolute inset-0 bg-cover bg-center opacity-15"
          style={{ backgroundImage: `url(https://images.unsplash.com/photo-1714601344981-75e003bc5d18?w=1400&q=80)` }}
        />
        <div className="absolute inset-0" style={{ background: 'linear-gradient(135deg, rgba(11,31,58,0.97) 0%, rgba(11,31,58,0.85) 60%, rgba(30,58,95,0.9) 100%)' }} />

        <div className="relative z-10 max-w-7xl mx-auto px-6 py-24 pt-36">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            {/* Left Content */}
            <div className="animate-fadeInUp">
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-semibold mb-8 border" style={{ background: 'rgba(212,175,55,0.15)', borderColor: 'rgba(212,175,55,0.4)', color: '#D4AF37' }}>
                <Shield size={15} />
                IRS Authorized E-File Provider
              </div>

              <h1 className="font-heading text-5xl lg:text-6xl font-extrabold text-white tracking-tight leading-tight mb-6">
                {t('heroTitle')} —{' '}
                <span style={{ color: '#D4AF37' }}>{t('heroTitleHighlight')}</span>
                <br />{lang === 'es' ? 'En Quienes Confiar' : 'You Can Trust'}
              </h1>

              <p className="text-lg text-blue-200 leading-relaxed mb-8 max-w-xl">
                {t('heroSub')}
              </p>

              <div className="flex flex-wrap gap-4 mb-10">
                <Link to="/book-appointment" data-testid="hero-book-btn">
                  <button className="btn-gold px-8 py-4 text-base font-bold" style={{ background: '#D4AF37', color: '#0B1F3A' }}>
                    {t('ctaBook')}
                    <ArrowRight size={18} />
                  </button>
                </Link>
                <Link to="/get-quote" data-testid="hero-quote-btn">
                  <button className="inline-flex items-center gap-2 px-8 py-4 rounded-full font-bold text-base text-white border-2 border-white/30 hover:bg-white/10 transition-all duration-300">
                    {t('ctaQuote')}
                    <ArrowRight size={18} />
                  </button>
                </Link>
              </div>

              <div className="flex flex-wrap gap-6">
                {[
                  { icon: Shield, text: 'IRS Authorized' },
                  { icon: Globe, text: 'All 50 States' },
                  { icon: Lock, text: 'Bank-Level Security' },
                ].map(({ icon: Icon, text }) => (
                  <div key={text} className="flex items-center gap-2">
                    <Icon size={16} style={{ color: '#D4AF37' }} />
                    <span className="text-blue-200 text-sm">{text}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Right — Image + Stats */}
            <div className="relative animate-slideInRight delay-300 hidden lg:block">
              <div className="relative">
                <img
                  src="https://images.unsplash.com/photo-1565688527174-775059ac429c?w=700&q=85"
                  alt="Professional tax consultation with diverse team"
                  className="w-full h-[480px] object-cover rounded-2xl"
                  style={{ boxShadow: '0 25px 50px rgba(0,0,0,0.4)' }}
                />
                {/* Floating cards */}
                <div className="absolute -bottom-6 -left-8 glass-card rounded-2xl p-5 animate-fadeInUp delay-500">
                  <div className="text-2xl font-heading font-bold text-white mb-1">$2,847</div>
                  <div className="text-xs text-blue-200 uppercase tracking-wider">Avg. Client Refund</div>
                  <div className="flex mt-2 gap-0.5">
                    {[...Array(5)].map((_, i) => <Star key={i} size={12} style={{ color: '#D4AF37', fill: '#D4AF37' }} />)}
                  </div>
                </div>
                <div className="absolute -top-6 -right-6 glass-card rounded-2xl p-5 animate-fadeInUp delay-400">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ background: '#D4AF37' }}>
                      <Award size={20} className="text-[#0B1F3A]" />
                    </div>
                    <div>
                      <div className="text-white font-semibold text-sm">IRS Certified</div>
                      <div className="text-blue-300 text-xs">E-File Provider</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Scroll indicator */}
        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2 animate-pulse-soft">
          <div className="w-px h-8 bg-white/30" />
          <div className="w-1.5 h-1.5 rounded-full bg-white/50" />
        </div>
      </section>

      {/* ─── TAX DEADLINE COUNTDOWN ─── */}
      <TaxDeadlineCountdown />

      {/* ─── TRUST BAR ─── */}
      <section className="bg-white border-b border-gray-100 py-6">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            {[
              { icon: Shield, text: 'IRS Authorized E-File Provider', color: '#0B1F3A' },
              { icon: Globe, text: 'Serving All 50 States', color: '#2563EB' },
              { icon: Award, text: 'Licensed & Credentialed Since 2021', color: '#D4AF37' },
              { icon: Lock, text: 'Encrypted & Confidential', color: '#10B981' },
            ].map(({ icon: Icon, text, color }) => (
              <div key={text} className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0" style={{ background: `${color}15` }}>
                  <Icon size={20} style={{ color }} />
                </div>
                <span className="text-sm font-medium text-[#0F172A]">{text}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ─── SERVICES ─── */}
      <section className="section-padding bg-[#F8FAFC]">
        <div className="container-max">
          <div className="text-center mb-14">
            <div className="label-uppercase text-[#2563EB] mb-3">Our Services</div>
            <h2 className="font-heading text-4xl font-bold text-[#0B1F3A] tracking-tight mb-4">
              Comprehensive Tax Solutions
            </h2>
            <p className="text-[#475569] text-lg max-w-2xl mx-auto">
              From individual returns to complex business filings — we handle it all with precision and care.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {services.map((service, i) => {
              const Icon = service.icon;
              return (
                <div
                  key={service.title}
                  className="bg-white rounded-2xl p-7 border border-gray-100 hover:shadow-floating hover:-translate-y-1 transition-all duration-300 group relative"
                >
                  {service.badge && (
                    <div className="absolute top-4 right-4 px-3 py-1 rounded-full text-xs font-semibold" style={{ background: '#D4AF3720', color: '#D4AF37' }}>
                      {service.badge}
                    </div>
                  )}
                  <div className="w-12 h-12 rounded-xl flex items-center justify-center mb-5 transition-colors duration-300"
                    style={{ background: `${service.color}15` }}>
                    <Icon size={24} style={{ color: service.color }} />
                  </div>
                  <h3 className="font-heading font-bold text-[#0B1F3A] text-lg mb-3">{service.title}</h3>
                  <p className="text-[#475569] text-sm leading-relaxed mb-5">{service.desc}</p>
                  <Link to="/services" className="flex items-center gap-1 text-sm font-semibold text-[#2563EB] group-hover:gap-2 transition-all duration-200">
                    Learn more <ChevronRight size={16} />
                  </Link>
                </div>
              );
            })}
          </div>

          <div className="text-center mt-10">
            <Link to="/services">
              <button className="btn-primary px-8 py-3.5">
                View All Services
                <ArrowRight size={18} />
              </button>
            </Link>
          </div>
        </div>
      </section>

      {/* ─── WHY CHOOSE US ─── */}
      <section className="section-padding bg-white">
        <div className="container-max">
          <div className="text-center mb-14">
            <div className="label-uppercase text-[#2563EB] mb-3">Why Choose Us</div>
            <h2 className="font-heading text-4xl font-bold text-[#0B1F3A] tracking-tight mb-4">
              The Personalized Alternative to DIY Software
            </h2>
            <p className="text-[#475569] text-lg max-w-2xl mx-auto">
              TurboTax gives you software. We give you a dedicated tax professional who knows your situation, anticipates your needs, and fights for every dollar you deserve.
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {whyChooseUs.map(({ icon: Icon, title, desc }) => (
              <div key={title} className="flex gap-4 p-6 rounded-xl border border-gray-100 hover:border-[#0B1F3A]/20 hover:shadow-card transition-all duration-200 bg-white">
                <div className="w-11 h-11 rounded-lg flex items-center justify-center flex-shrink-0" style={{ background: '#0B1F3A10' }}>
                  <Icon size={22} style={{ color: '#0B1F3A' }} />
                </div>
                <div>
                  <div className="font-heading font-semibold text-[#0B1F3A] mb-1">{title}</div>
                  <div className="text-[#475569] text-sm leading-relaxed">{desc}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ─── FOUNDER SECTION ─── */}
      <FounderSection variant="compact" />

      {/* ─── HOW IT WORKS ─── */}
      <section className="section-padding" style={{ background: '#0B1F3A' }}>
        <div className="container-max">
          <div className="text-center mb-14">
            <div className="label-uppercase mb-3" style={{ color: '#D4AF37' }}>The Process</div>
            <h2 className="font-heading text-4xl font-bold text-white tracking-tight mb-4">
              {t('processTitle')}
            </h2>
            <p className="text-blue-200 text-lg max-w-xl mx-auto">
              Simple, transparent, stress-free. Get your taxes done in four easy steps.
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {steps.map((step, i) => (
              <div key={step.num} className="relative">
                <div className="glass-card rounded-2xl p-7 hover:bg-white/20 transition-all duration-300">
                  <div className="font-heading text-5xl font-bold mb-4 opacity-50" style={{ color: '#D4AF37' }}>{step.num}</div>
                  <h3 className="font-heading font-bold text-white text-lg mb-3">{step.title}</h3>
                  <p className="text-blue-200 text-sm leading-relaxed">{step.desc}</p>
                </div>
                {i < steps.length - 1 && (
                  <div className="hidden lg:flex absolute top-1/2 -right-3 -translate-y-1/2 z-10">
                    <ArrowRight size={20} style={{ color: '#D4AF37' }} />
                  </div>
                )}
              </div>
            ))}
          </div>

          <div className="text-center mt-12">
            <Link to="/how-it-works">
              <button className="inline-flex items-center gap-2 px-8 py-4 rounded-full font-semibold text-[#0B1F3A] hover:shadow-xl transition-all duration-300" style={{ background: '#D4AF37' }}>
                Learn More About Our Process
                <ArrowRight size={18} />
              </button>
            </Link>
          </div>
        </div>
      </section>

      {/* ─── TESTIMONIALS ─── */}
      <section className="section-padding bg-[#F8FAFC]">
        <div className="container-max">
          <div className="text-center mb-14">
            <div className="label-uppercase text-[#2563EB] mb-3">Client Stories</div>
            <h2 className="font-heading text-4xl font-bold text-[#0B1F3A] tracking-tight">
              {t('testimonialsTitle')}
            </h2>
            <p className="text-[#475569] mt-3">Real feedback from clients across all 50 states</p>
          </div>

          <div className="relative">
            {/* Navigation Arrows */}
            <button 
              onClick={prevReviews}
              className="absolute left-0 top-1/2 -translate-y-1/2 -translate-x-4 z-10 w-10 h-10 rounded-full bg-white shadow-lg flex items-center justify-center hover:bg-gray-50 transition-colors hidden md:flex"
              aria-label="Previous reviews"
            >
              <ChevronLeft size={20} className="text-[#0B1F3A]" />
            </button>
            <button 
              onClick={nextReviews}
              className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-4 z-10 w-10 h-10 rounded-full bg-white shadow-lg flex items-center justify-center hover:bg-gray-50 transition-colors hidden md:flex"
              aria-label="Next reviews"
            >
              <ChevronRight size={20} className="text-[#0B1F3A]" />
            </button>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 transition-all duration-500">
              {visibleTestimonials.map((t2, i) => (
                <div key={`${t2.name}-${i}`} className="bg-white rounded-2xl p-6 border border-gray-100 hover:shadow-floating transition-all duration-300">
                  <div className="flex gap-0.5 mb-4">
                    {[...Array(t2.rating)].map((_, j) => (
                      <Star key={j} size={14} style={{ color: '#D4AF37', fill: '#D4AF37' }} />
                    ))}
                  </div>
                  <p className="text-[#0F172A] leading-relaxed mb-5 text-sm line-clamp-4">"{t2.text}"</p>
                  <div className="flex items-center gap-3">
                    <div className="w-9 h-9 rounded-full flex items-center justify-center font-bold text-white text-xs" style={{ background: '#0B1F3A' }}>
                      {t2.name.charAt(0)}
                    </div>
                    <div>
                      <div className="font-semibold text-[#0B1F3A] text-sm">{t2.name}</div>
                      <div className="text-[#475569] text-xs">{t2.role}</div>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Dots Indicator */}
            <div className="flex justify-center gap-2 mt-8">
              {Array.from({ length: Math.ceil(testimonials.length / 4) }).map((_, i) => (
                <button
                  key={i}
                  onClick={() => setCurrentReviewIndex(i * 4)}
                  className={`w-2 h-2 rounded-full transition-all duration-300 ${
                    Math.floor(currentReviewIndex / 4) === i ? 'bg-[#0B1F3A] w-6' : 'bg-gray-300'
                  }`}
                  aria-label={`Go to review set ${i + 1}`}
                />
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ─── STATS ─── */}
      <section className="py-16 bg-white">
        <div className="container-max px-6">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-8">
            {stats.map(({ value, label, sub }) => (
              <div key={label} className="text-center">
                <div className="font-heading text-5xl font-extrabold text-[#0B1F3A] tracking-tight mb-2">{value}</div>
                <div className="font-semibold text-[#0F172A] text-sm mb-1">{label}</div>
                <div className="text-[#94A3B8] text-xs">{sub}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ─── BLOG PREVIEW ─── */}
      {blogPosts.length > 0 && (
        <section className="section-padding bg-[#F8FAFC]">
          <div className="container-max">
            <div className="flex items-center justify-between mb-12">
              <div>
                <div className="label-uppercase text-[#2563EB] mb-2">Tax Insights</div>
                <h2 className="font-heading text-3xl font-bold text-[#0B1F3A] tracking-tight">Latest from Our Blog</h2>
              </div>
              <Link to="/blog" className="hidden sm:flex items-center gap-2 text-sm font-semibold text-[#2563EB] hover:gap-3 transition-all duration-200">
                View All Articles <ArrowRight size={16} />
              </Link>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {blogPosts.map((post) => (
                <Link key={post.id} to={`/blog/${post.slug}`} className="group">
                  <div className="bg-white rounded-2xl overflow-hidden border border-gray-100 hover:shadow-floating transition-all duration-300 hover:-translate-y-1">
                    {post.featured_image && (
                      <img src={post.featured_image} alt={post.title} className="w-full h-44 object-cover" />
                    )}
                    <div className="p-6">
                      <div className="label-uppercase text-[#2563EB] mb-2">{post.category}</div>
                      <h3 className="font-heading font-bold text-[#0B1F3A] text-lg leading-snug mb-3 group-hover:text-[#2563EB] transition-colors duration-200">
                        {post.title}
                      </h3>
                      <p className="text-[#475569] text-sm leading-relaxed line-clamp-2">{post.excerpt}</p>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* ─── CTA ─── */}
      <section className="section-padding relative overflow-hidden" style={{ background: '#0B1F3A' }}>
        <div className="absolute top-0 right-0 w-96 h-96 rounded-full opacity-5" style={{ background: '#D4AF37', filter: 'blur(80px)' }} />
        <div className="relative z-10 container-max text-center">
          <div className="label-uppercase mb-4" style={{ color: '#D4AF37' }}>Get Started Today</div>
          <h2 className="font-heading text-4xl lg:text-5xl font-extrabold text-white tracking-tight mb-6">
            Ready to Maximize Your Tax Return?
          </h2>
          <p className="text-blue-200 text-lg max-w-2xl mx-auto mb-10">
            Join hundreds of clients who trust MaTaxPreparer LLC for fast, accurate, and affordable tax preparation — nationwide.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <Link to="/book-appointment" data-testid="cta-book-btn">
              <button className="inline-flex items-center gap-2 px-10 py-4 rounded-full font-bold text-base text-[#0B1F3A] hover:shadow-xl hover:-translate-y-0.5 transition-all duration-300" style={{ background: '#D4AF37' }}>
                Book Free Consultation
                <ArrowRight size={18} />
              </button>
            </Link>
            <a href="tel:7702356999" data-testid="cta-phone-btn">
              <button className="inline-flex items-center gap-2 px-10 py-4 rounded-full font-bold text-base text-white border-2 border-white/30 hover:bg-white/10 transition-all duration-300">
                <Phone size={18} />
                770-235-6999
              </button>
            </a>
          </div>
          <div className="mt-8 text-blue-300 text-sm">
            Available Mon–Fri 9AM–5PM, Sat 10AM–2PM (EST) · All 50 States
          </div>
        </div>
      </section>
    </div>
  );
};

export default HomePage;
